import { cn } from "@/lib/cn";
import { type CSSProperties, forwardRef } from "react";

export type StatusDotVariant =
  | "ok"
  | "warn"
  | "err"
  | "down"
  | "idle"
  | "banned"
  | "disconnected"
  // IR-1769 (O-4): an operator Stop, its own status.
  | "stopped"
  | "inactive"
  // IR-1487: Archived is a deliberate, benign state — not an error (red) and
  // not the attention state (orange). The backend declares its colour "blue";
  // cyan is this kit's blue token.
  | "archived";

export interface StatusDotProps {
  /** Visual state of the dot */
  variant?: StatusDotVariant;
  /** Diameter in px */
  size?: number;
  /** Whether the dot pulses (uses iris-pulse animation) */
  pulse?: boolean;
  className?: string;
  style?: CSSProperties;
}

const COLOR_MAP: Record<StatusDotVariant, string> = {
  ok: "var(--iris-neon-green)",
  warn: "var(--iris-neon-orange)",
  err: "var(--iris-neon-pink)",
  down: "var(--iris-neon-red)",
  banned: "var(--iris-neon-red)",
  disconnected: "var(--iris-neon-red)",
  stopped: "var(--iris-neon-magenta)",
  inactive: "var(--iris-neon-yellow)",
  idle: "var(--iris-fg-4)",
  archived: "var(--iris-neon-cyan)",
};

const GLOW_MAP: Record<StatusDotVariant, string> = {
  ok: "0 0 8px var(--iris-neon-green), 0 0 18px rgba(57,255,136,.75), 0 0 32px rgba(57,255,136,.4)",
  warn: "0 0 8px var(--iris-neon-orange), 0 0 18px rgba(255,153,51,.8), 0 0 32px rgba(255,153,51,.45)",
  err: "0 0 8px var(--iris-neon-pink), 0 0 18px rgba(255,77,210,.75), 0 0 32px rgba(255,77,210,.4)",
  down: "0 0 8px var(--iris-neon-red), 0 0 18px rgba(255,51,85,.8), 0 0 32px rgba(255,51,85,.45)",
  banned: "0 0 8px var(--iris-neon-red), 0 0 18px rgba(255,51,85,.8), 0 0 32px rgba(255,51,85,.45)",
  disconnected:
    "0 0 8px var(--iris-neon-red), 0 0 18px rgba(255,51,85,.8), 0 0 32px rgba(255,51,85,.45)",
  stopped:
    "0 0 8px var(--iris-neon-magenta), 0 0 18px rgba(255,46,166,.75), 0 0 32px rgba(255,46,166,.4)",
  inactive:
    "0 0 8px var(--iris-neon-yellow), 0 0 18px rgba(234,255,42,.6), 0 0 28px rgba(234,255,42,.3)",
  idle: "none",
  archived:
    "0 0 8px var(--iris-neon-cyan), 0 0 18px rgba(34,211,255,.6), 0 0 28px rgba(34,211,255,.3)",
};

export const StatusDot = forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ variant = "ok", size = 8, pulse = true, className, style }, ref) => {
    return (
      <span
        ref={ref}
        // IR-1228 r5: a stable hook for the dot. Callers used to find it with
        // `.iris-pulse`, which is the ANIMATION class — so turning the pulse off
        // for one badge silently broke tests asserting that badge's COLOUR, a
        // fact that has nothing to do with whether it animates.
        data-testid="status-dot"
        className={cn(pulse && "iris-pulse", className)}
        style={{
          display: "inline-block",
          width: size,
          height: size,
          borderRadius: 999,
          background: COLOR_MAP[variant],
          boxShadow: GLOW_MAP[variant],
          ...style,
        }}
      />
    );
  },
);
StatusDot.displayName = "StatusDot";
